import { useUnit } from 'effector-react';
import { Languages, LogOut, Monitor, Moon, Settings2, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  $locale,
  $theme,
  localeChanged,
  themeChanged,
  type ThemePreference,
} from '../../entities/preferences';
import { $user, signOutFx } from '../../entities/session';
import { LANGUAGE_NAME, LOCALES } from '../../shared/lib/locale';
import {
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
  Tooltip,
} from '../../shared/ui';

const THEME_ICON: Record<ThemePreference, typeof Sun> = { system: Monitor, dark: Moon, light: Sun };

/** Theme, language and sign-out, in the top bar. */
export function PreferencesMenu() {
  const { t } = useTranslation();
  const [theme, setTheme, locale, setLocale, user, signOut] = useUnit([
    $theme,
    themeChanged,
    $locale,
    localeChanged,
    $user,
    signOutFx,
  ]);
  return (
    <Menu>
      <Tooltip content={t('preferences.menu')}>
        <MenuTrigger asChild>
          <IconButton label={t('preferences.menu')}>
            <Settings2 />
          </IconButton>
        </MenuTrigger>
      </Tooltip>
      <MenuContent>
        <MenuLabel>{t('preferences.theme')}</MenuLabel>
        <MenuRadioGroup
          value={theme}
          onValueChange={(value) => {
            setTheme(value as ThemePreference);
          }}
        >
          {(['system', 'dark', 'light'] as const).map((value) => {
            const Icon = THEME_ICON[value];
            return (
              <MenuRadioItem key={value} value={value} icon={<Icon />}>
                {t(`preferences.themes.${value}`)}
              </MenuRadioItem>
            );
          })}
        </MenuRadioGroup>
        <MenuSeparator />
        <MenuLabel>{t('preferences.language')}</MenuLabel>
        <MenuRadioGroup
          value={locale}
          onValueChange={(value) => {
            const next = LOCALES.find((l) => l === value);
            if (next) setLocale(next);
          }}
        >
          {LOCALES.map((value) => (
            <MenuRadioItem key={value} value={value} icon={<Languages />}>
              {LANGUAGE_NAME[value]}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
        {user && (
          <>
            <MenuSeparator />
            <MenuLabel>{user.email}</MenuLabel>
            <MenuItem
              icon={<LogOut />}
              onSelect={() => {
                void signOut().catch(() => undefined);
              }}
            >
              {t('session.signOut')}
            </MenuItem>
          </>
        )}
      </MenuContent>
    </Menu>
  );
}
